/**
 * AuditLogViewElement — kreator formatek: klocek palety reprezentujący tabelkę
 * historii zmian rekordu (`audit_log_view`, patrz `RecursiveFormRenderer`
 * PLACEHOLDER_TYPES).
 *
 * Mirror `AttachmentsViewElement` / `DocumentItemsViewElement` (patrz nagłówki tamtych
 * plików) — na REALNEJ stronie rekordu ten typ jest dispatchowany WYŁĄCZNIE przez
 * `contextRenderers['audit_log_view']` (widget `SystemModuleAuditTab`). Ten plik obsługuje
 * TYLKO podgląd w canvasie buildera (`mode='builder'`), gdzie nie ma prawdziwego rekordu.
 *
 * PO CO: audyt był dotąd jedynym widokiem systemowym dostępnym wyłącznie jako CAŁA
 * ZAKŁADKA (typ zakładki z seeda, `buildTabChildren` w `apps/api/lib/seed-system-modules.ts`).
 * Wystawienie go w palecie zamyka rozjazd względem załączników, podglądu dokumentu
 * i pozycji dokumentu: tenant stawia historię zmian TAM, GDZIE CHCE — w zakładce „Dane",
 * w sekcji, w akordeonie — a jeśli woli osobną zakładkę, dodaje zakładkę i wrzuca do niej
 * ten klocek. Kształt jest decyzją układu, nie kodu strony.
 *
 * `allowedModules` — PUSTE od kartoteka-blokify Towarów (2026-08-27): wszystkie kartoteki
 * mają dziś WŁASNY, nazwany klocek audytu per kartoteka (`EntityAuditViewElements` —
 * contractors/employees/companies/users + products=`product_audit_view` +
 * tasks=`task_audit_view`), więc generyk
 * NIE pojawia się już w żadnej palecie (inaczej products pokazywałby DWA „Historia zmian").
 * Render/dispatch generyka na stronach detalu zostaje jako FALLBACK dla layoutów, które
 * wciąż trzymają `audit_log_view` (sprzed migracji na nazwane bloki) — to niezależne od
 * `allowedModules` (ono steruje tylko widocznością w palecie).
 *
 * `render` jest wołany jak ZWYKŁA FUNKCJA (nie komponent) — patrz
 * `LayoutElementRegistry.render` — więc żaden hook nie może być wywołany BEZPOŚREDNIO
 * w jej ciele: `i18n.t` standalone zamiast `useTranslation`.
 *
 * `label` jest literalnym stringiem, zgodnie z precedensem wszystkich pozostałych klocków
 * palety (paleta czyta `def.label` wprost). Klucz i18n obsługuje mock w canvasie. Przejście
 * całej palety na `labelKey` + `t()` to osobne zadanie — patrz backlog konwergencji;
 * `i18n.t()` przy rejestracji byłoby błędem, bo policzyłoby się raz i nie reagowało na
 * zmianę języka.
 */

import { History } from 'lucide-react';

import type { LayoutElementDefinition } from '@/components/fields/LayoutElementRegistry';
import i18n from '@/i18n';

/**
 * PUSTE — generyk audytu nie jest już oferowany w ŻADNEJ palecie kartoteki. Każda kartoteka
 * ma własny, nazwany klocek audytu (`EntityAuditViewElements`), w tym Towary
 * (`product_audit_view`, kartoteka-blokify 2026-08-27) i Zadania (`task_audit_view`). Pusta lista = ukryty wszędzie
 * (patrz filtr palety: `[]` jest truthy, więc `!allowedModules` = false, a `includes` = false).
 * Dispatch/render generyka na stronach detalu zostaje jako fallback dla starych layoutów.
 */
const AUDIT_VIEW_ALLOWED_MODULES: string[] = [];

export const AuditLogViewElement: LayoutElementDefinition = {
  type: 'audit_log_view',
  category: 'tresc',
  isContainer: false,
  label: 'Historia zmian',
  icon: 'History',
  allowedModules: AUDIT_VIEW_ALLOWED_MODULES,
  defaultSettings: () => ({}),
  render: ({ element }) => (
    <div
      className="rounded-lg border border-dashed border-border-secondary bg-bg-secondary p-4 text-center"
      data-testid={`audit-log-view-element-${element.id}`}
      data-element-type="audit_log_view"
    >
      <History className="mx-auto h-5 w-5 text-text-muted" aria-hidden="true" />
      <p className="mt-1 text-xs font-medium text-text-secondary">
        {i18n.t('tenant.systemModuleAudit.paletteLabel', { defaultValue: 'Historia zmian' })}
      </p>
    </div>
  ),
};
